import { CURRENCIES } from '@/lib/currency';
import { summarizeFormErrors, type FormErrors } from '@/lib/form-errors';
import type { UpdateVendorPayload, Vendor } from '@/lib/types';

/** Human labels for the error summary, keyed by API field name. */
export const VENDOR_FIELD_LABELS: Record<string, string> = {
  name: 'Vendor name',
  vendor_number: 'Vendor number',
  tax_id: 'Tax ID',
  status: 'Status',
  default_payment_terms: 'Payment terms',
  default_currency: 'Currency',
  preferred: 'Preferred vendor',
  contact_name: 'Contact name',
  contact_phone: 'Phone',
  contact_email: 'Email',
  website: 'Website',
  address: 'Street address',
  city: 'City',
  state_province: 'State',
  postal_code: 'Zip code',
  country: 'Country',
  notes: 'Notes',
};

function isListedCurrency(code: string): boolean {
  return CURRENCIES.some((currency) => currency.code === code);
}

/**
 * The edit form's starting values for a loaded vendor.
 *
 * A currency outside the dropdown's list (free text saved before currency
 * became a dropdown, e.g. "R" or "usd") starts blank. Radix Select would
 * otherwise swap it for "" itself as soon as the value arrives.
 */
export function vendorFormValues(vendor: Vendor): UpdateVendorPayload {
  const currency = vendor.default_currency.trim().toUpperCase();
  return {
    name: vendor.name,
    vendor_number: vendor.vendor_number,
    tax_id: vendor.tax_id,
    default_payment_terms: vendor.default_payment_terms,
    default_currency: isListedCurrency(currency) ? currency : '',
    status: vendor.status,
    preferred: vendor.preferred,
    address: vendor.address,
    city: vendor.city,
    state_province: vendor.state_province,
    country: vendor.country,
    postal_code: vendor.postal_code,
    contact_name: vendor.contact_name,
    contact_email: vendor.contact_email,
    contact_phone: vendor.contact_phone,
    website: vendor.website,
    notes: vendor.notes,
  };
}

/**
 * Only the fields the user changed. The edit form PATCHes just these so an
 * untouched value the API now rejects (like a blank currency) can't block an
 * unrelated edit.
 */
export function changedVendorFields(
  form: UpdateVendorPayload,
  saved: UpdateVendorPayload
): UpdateVendorPayload {
  const changes: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(form)) {
    if (value !== saved[field as keyof UpdateVendorPayload]) changes[field] = value;
  }
  return changes as UpdateVendorPayload;
}

/**
 * Toast text for a failed save. Field errors show under their fields, so the
 * toast carries everything else, or points at the fields if that's all.
 */
export function vendorErrorToast(errors: FormErrors, failure: string): string {
  const offForm: FormErrors = {
    ...errors,
    fields: Object.fromEntries(
      Object.entries(errors.fields).filter(([field]) => !(field in VENDOR_FIELD_LABELS))
    ),
  };
  const lines = summarizeFormErrors(offForm, VENDOR_FIELD_LABELS);
  return lines.length ? lines.join(' ') : `${failure} Check the highlighted fields.`;
}
